import { toast } from 'react-toastify';
import {
  Box,
  Button,
  ButtonGroup,
  Container,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FileBase64 from 'react-file-base64';

import { API_URL } from '../../api';
import QuillEditor from '../../utils/QuillEditor';
import Cookies from 'universal-cookie';
import showLoader from 'src/components/loader';
import Iconify from '../../components/iconify';

const cookies = new Cookies();

const LEVELS = [
  'KS1 (5-7 yrs)',
  'KS2 (7-11 yrs)',
  'KS3 (11-14 yrs)',
  'GCSE (14-16 yrs)',
];

function UpdateProduct() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);

  // Basic info
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [longDescription, setLongDescription] = useState('');

  // Pricing
  const [price, setPrice] = useState('');
  const [comparePrice, setComparePrice] = useState('');

  // Categories
  const [subject, setSubject] = useState('');
  const [level, setLevel] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [subjects, setSubjects] = useState([]);

  // Settings
  const [status, setStatus] = useState('draft');

  // Files
  const [existingMainImage, setExistingMainImage] = useState(null);
  const [mainImage, setMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState('');
  const [existingPreviewImages, setExistingPreviewImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [removePreviewIds, setRemovePreviewIds] = useState([]);
  const [existingFile, setExistingFile] = useState(null);
  const [productFile, setProductFile] = useState(null);
  const [productFileName, setProductFileName] = useState('');

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    axios.get(`${API_URL}/api/subjects?activeOnly=true`)
      .then((r) => setSubjects(Array.isArray(r.data) ? r.data.map((s) => s.name).filter(Boolean) : []))
      .catch(() => {});
  }, []);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/admin/${id}`, {
        headers: {
          Authorization: cookies.get('TOKEN'),
        },
      });

      const product = response.data.data;

      setTitle(product.title || '');
      setSlug(product.slug || '');
      setShortDescription(product.shortDescription || '');
      setLongDescription(product.longDescription || '');
      setPrice((product.price / 100).toFixed(2));
      setComparePrice(product.comparePrice ? (product.comparePrice / 100).toFixed(2) : '');
      setSubject(product.subject || '');
      setLevel(product.level || '');
      setTags(product.tags || []);
      setStatus(product.status || 'draft');

      if (product.images && product.images.length > 0) {
        setExistingMainImage(product.images[0]);
        setMainImagePreview(product.images[0].url);
      }

      if (product.previewImages) {
        setExistingPreviewImages(product.previewImages);
      }

      if (product.file) {
        setExistingFile(product.file);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
      navigate('/products');
    }
  };

  const handleMainImageUpload = (file) => {
    setMainImage(file.base64);
    setMainImagePreview(file.base64);
  };

  const handlePreviewImagesUpload = (files) => {
    const newPreviews = files.map((file) => file.base64);
    setPreviewImages([...previewImages, ...newPreviews]);
  };

  const removeNewPreviewImage = (index) => {
    setPreviewImages(previewImages.filter((_, i) => i !== index));
  };

  const removeExistingPreviewImage = (publicId) => {
    setRemovePreviewIds([...removePreviewIds, publicId]);
    setExistingPreviewImages(existingPreviewImages.filter((img) => img.publicId !== publicId));
  };

  const handleProductFileUpload = (file) => {
    setProductFile(file.base64);
    setProductFileName(file.name);
    setExistingFile(null);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!title) {
      toast.error('Title is required');
      return;
    }
    if (!shortDescription) {
      toast.error('Short description is required');
      return;
    }
    if (!longDescription) {
      toast.error('Full description is required');
      return;
    }
    if (!price || isNaN(price) || parseFloat(price) <= 0) {
      toast.error('Valid price is required');
      return;
    }
    if (!subject) {
      toast.error('Subject is required');
      return;
    }
    if (!level) {
      toast.error('Level is required');
      return;
    }

    showLoader(true);

    try {
      const priceInPence = Math.round(parseFloat(price) * 100);
      const comparePriceInPence = comparePrice ? Math.round(parseFloat(comparePrice) * 100) : null;

      const updateData = {
        title,
        slug,
        shortDescription,
        longDescription,
        price: priceInPence,
        comparePrice: comparePriceInPence,
        subject,
        level,
        tags,
        status,
      };

      // Only include new images if uploaded
      if (mainImage) {
        updateData.mainImage = mainImage;
      }

      if (previewImages.length > 0) {
        updateData.previewImages = previewImages;
      }

      if (removePreviewIds.length > 0) {
        updateData.removePreviewIds = removePreviewIds;
      }

      if (productFile) {
        updateData.productFile = productFile;
        updateData.productFileName = productFileName;
      }

      await axios.patch(`${API_URL}/api/products/${id}`, updateData, {
        headers: {
          Authorization: cookies.get('TOKEN'),
        },
      });

      toast.success('Product updated successfully');
      navigate('/products');
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error(error.response?.data?.message || 'Failed to update product');
    }

    showLoader(false);
  };

  const back = (e) => {
    e.preventDefault();
    navigate('/products');
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Inter', fontWeight: '600' }}>
          Edit Product
        </Typography>
      </Stack>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>

                <TextField
                  fullWidth
                  label="Product Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="Short Description"
                  value={shortDescription}
                  onChange={(e) => setShortDescription(e.target.value)}
                  margin="normal"
                  multiline
                  rows={2}
                  helperText="Brief description for product cards (max 300 characters)"
                  inputProps={{ maxLength: 300 }}
                  required
                />

                <div style={{ marginTop: 16 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Full Description
                  </Typography>
                  <QuillEditor value={longDescription} setDesc={setLongDescription} />
                </div>
              </CardContent>
            </Card>
          </Grid>

          {/* Pricing */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pricing
                </Typography>

                <TextField
                  fullWidth
                  label="Price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  margin="normal"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  required
                />

                <TextField
                  fullWidth
                  label="Compare at Price (optional)"
                  value={comparePrice}
                  onChange={(e) => setComparePrice(e.target.value)}
                  margin="normal"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Original price to show discount"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Categories */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Categories
                </Typography>

                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Subject</InputLabel>
                  <Select value={subject} onChange={(e) => setSubject(e.target.value)} label="Subject">
                    {subjects.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Age Group</InputLabel>
                  <Select value={level} onChange={(e) => setLevel(e.target.value)} label="Age Group">
                    {LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Tags */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Tags
                </Typography>

                <Box display="flex" gap={1} mb={2}>
                  <TextField
                    fullWidth
                    label="Add Tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    size="small"
                  />
                  <Button variant="outlined" onClick={handleAddTag}>
                    Add
                  </Button>
                </Box>

                <Box display="flex" flexWrap="wrap" gap={1}>
                  {tags.map((tag) => (
                    <Chip key={tag} label={tag} onDelete={() => handleRemoveTag(tag)} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Settings
                </Typography>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Main Image */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Main Product Image
                </Typography>

                {mainImagePreview && (
                  <Box mb={2}>
                    <img
                      src={mainImagePreview}
                      alt="Preview"
                      style={{ width: '100%', maxWidth: 200, borderRadius: 8 }}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FileBase64 multiple={false} onDone={handleMainImageUpload} />
                  <Typography variant="caption" color="textSecondary">
                    Upload new image to replace existing
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Preview Images */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Preview Images
                </Typography>

                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  {existingPreviewImages.map((img) => (
                    <Box key={img.publicId} position="relative">
                      <img
                        src={img.url}
                        alt="Preview"
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeExistingPreviewImage(img.publicId)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' },
                        }}
                      >
                        <Iconify icon="mdi:close" width={16} />
                      </IconButton>
                    </Box>
                  ))}
                  {previewImages.map((img, index) => (
                    <Box key={`new-${index}`} position="relative">
                      <img
                        src={img}
                        alt={`New Preview ${index + 1}`}
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeNewPreviewImage(index)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' },
                        }}
                      >
                        <Iconify icon="mdi:close" width={16} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FileBase64 multiple={true} onDone={handlePreviewImagesUpload} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Product File */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Product File
                </Typography>

                {existingFile && (
                  <Box mb={2}>
                    <Chip
                      icon={<Iconify icon="mdi:file-document" />}
                      label={`Current: ${existingFile.filename} (${existingFile.fileType.toUpperCase()})`}
                      color="primary"
                    />
                  </Box>
                )}

                {productFileName && (
                  <Box mb={2}>
                    <Chip
                      icon={<Iconify icon="mdi:file-document" />}
                      label={`New: ${productFileName}`}
                      onDelete={() => {
                        setProductFile(null);
                        setProductFileName('');
                      }}
                      color="success"
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FileBase64 multiple={false} onDone={handleProductFileUpload} />
                  <Typography variant="caption" color="textSecondary">
                    Upload new file to replace existing
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <ButtonGroup sx={{ justifyContent: 'flex-end', width: '100%' }}>
              <Button onClick={back} style={{ fontFamily: 'Inter', minWidth: '90px' }}>
                Cancel
              </Button>
              <Button variant="contained" type="submit" style={{ fontFamily: 'Inter', minWidth: '90px' }}>
                Update Product
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default UpdateProduct;

const useStyles = makeStyles((theme) =>
  createStyles({
    // Styles if needed
  })
);
